/** Extensions explicitly rejected when publishing audit toolkit binaries. */
const BLOCKED_EXTENSIONS = [
  '.zip', '.tar', '.gz', '.tgz', '.bz2', '.xz', '.7z',
  '.pdf', '.txt', '.md', '.json', '.xml', '.html', '.htm',
  '.js', '.ts', '.tsx', '.jsx', '.py', '.sh', '.ps1', '.bat', '.cmd',
  '.dll', '.msi', '.msm', '.msp', '.dmg', '.pkg', '.deb', '.rpm', '.app',
] as const;

const UNIX_EXECUTABLE_MIME_TYPES = new Set([
  'application/x-executable',
  'application/x-mach-binary',
  'application/x-elf',
  'application/x-sharedlib',
  'application/octet-stream',
]);

/**
 * Do not set `<input accept>` for release binaries: on macOS/Chrome it greys out
 * extensionless Unix executables even with `/*`. Gate uploads with
 * {@link isAllowedReleaseBinary} instead.
 */

export function getReleaseBinaryContentType(fileName: string): string {
  if (fileName.toLowerCase().endsWith('.exe')) {
    return 'application/vnd.microsoft.portable-executable';
  }
  return 'application/octet-stream';
}

/** Windows `.exe` or Unix executable (typically no extension, e.g. `audit-toolkit`). */
export function isAllowedReleaseBinary(file: File): boolean {
  const name = file.name.trim();
  if (!name) return false;

  const lower = name.toLowerCase();
  if (lower.endsWith('.exe')) return true;
  if (BLOCKED_EXTENSIONS.some(ext => lower.endsWith(ext))) return false;

  const lastDot = name.lastIndexOf('.');
  // No extension, or leading dot only (hidden unix-style names)
  if (lastDot <= 0) return true;

  // Rare case: extension present but OS reports a Unix/binary MIME type
  return Boolean(file.type && UNIX_EXECUTABLE_MIME_TYPES.has(file.type));
}
